/**
 * Graph Utilities
 * Helper functions for analyzing node graphs and finding paths
 */

import type { Node, Edge } from '@xyflow/react';

/**
 * Find all nodes that produce images (directly or indirectly connected)
 * Returns nodes sorted by distance (closest first), then by connection order (most recent first)
 */
export function findClosestImageNodes(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[]
): Node[] {
  // Build adjacency list for reverse traversal (from target backwards to sources)
  const incomingEdges = new Map<string, Array<{ sourceId: string; timestamp: number }>>();
  
  edges.forEach((edge) => {
    if (!incomingEdges.has(edge.target)) {
      incomingEdges.set(edge.target, []);
    }
    // Use edge creation timestamp if available, otherwise use 0
    const timestamp = (edge as any).data?.timestamp || 0;
    incomingEdges.get(edge.target)!.push({ sourceId: edge.source, timestamp });
  });

  // BFS to find all reachable nodes with their distances
  const distances = new Map<string, { distance: number; timestamp: number }>();
  const queue: Array<{ nodeId: string; distance: number; timestamp: number }> = [
    { nodeId: targetNodeId, distance: 0, timestamp: Date.now() }
  ];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const { nodeId, distance, timestamp } = queue.shift()!;
    
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    
    distances.set(nodeId, { distance, timestamp });

    // Explore incoming edges (nodes that connect TO this node)
    const incoming = incomingEdges.get(nodeId) || [];
    incoming.forEach(({ sourceId, timestamp: edgeTimestamp }) => {
      if (!visited.has(sourceId)) {
        queue.push({
          nodeId: sourceId,
          distance: distance + 1,
          timestamp: edgeTimestamp || timestamp
        });
      }
    });
  }

  // Filter for image-producing nodes
  const imageNodes = nodes.filter((node) => {
    const hasDistance = distances.has(node.id) && node.id !== targetNodeId;
    if (!hasDistance) return false;

    // Check if node produces images
    const nodeType = node.type;
    const nodeData = node.data as any;

    // Image Panel nodes
    if (nodeType === 'imagePanel' && nodeData.imageUrl) {
      return true;
    }

    // Image Prompt nodes (Art Director, etc.)
    if (nodeType === 'imagePrompt' && nodeData.generatedImageUrl) {
      return true;
    }

    // Custom Output nodes displaying images
    if (nodeType === 'customOutput') {
      const pages = nodeData.pages || [];
      const currentPage = pages[nodeData.currentPageIndex || 0];
      if (currentPage?.type === 'image' && currentPage.content) {
        return true;
      }
      // Legacy: check direct content
      if (nodeData.type === 'image' && nodeData.content) {
        return true;
      }
    }

    // Custom nodes that output images
    if (node.type?.startsWith('custom-') && nodeData.outputImageUrl) {
      return true;
    }

    return false;
  });

  // Sort by distance (closest first), then by timestamp (most recent first)
  imageNodes.sort((a, b) => {
    const distA = distances.get(a.id)!.distance;
    const distB = distances.get(b.id)!.distance;
    
    if (distA !== distB) {
      return distA - distB; // Closer nodes first
    }
    
    // Same distance - sort by timestamp (most recent first)
    const timeA = distances.get(a.id)!.timestamp;
    const timeB = distances.get(b.id)!.timestamp;
    return timeB - timeA; // More recent connections first
  });

  return imageNodes;
}

/**
 * Get the URL of the image from a node
 */
export function getImageUrlFromNode(node: Node): string | null {
  const nodeData = node.data as any;
  const nodeType = node.type;

  // Image Panel nodes
  if (nodeType === 'imagePanel' && nodeData.imageUrl) {
    return nodeData.imageUrl;
  }

  // Image Prompt nodes
  if (nodeType === 'imagePrompt' && nodeData.generatedImageUrl) {
    return nodeData.generatedImageUrl;
  }

  // Custom Output nodes
  if (nodeType === 'customOutput') {
    const pages = nodeData.pages || [];
    const currentPage = pages[nodeData.currentPageIndex || 0];
    if (currentPage?.type === 'image' && currentPage.content) {
      return currentPage.content;
    }
    // Legacy: check direct content
    if (nodeData.type === 'image' && nodeData.content) {
      return nodeData.content;
    }
  }

  // Custom nodes
  if (node.type?.startsWith('custom-') && nodeData.outputImageUrl) {
    return nodeData.outputImageUrl;
  }

  return null;
}
